import { useState, useEffect } from 'react';

export interface OnlineStatusHookReturn {
  isOnline: boolean;
  isOffline: boolean;
  hasBeenOffline: boolean;
  lastOnlineTime: Date | null;
  retryConnection: () => Promise<boolean>;
}

export const useOnlineStatus = (): OnlineStatusHookReturn => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [hasBeenOffline, setHasBeenOffline] = useState<boolean>(false);
  const [lastOnlineTime, setLastOnlineTime] = useState<Date | null>(
    navigator.onLine ? new Date() : null
  );

  // Test connection by attempting to fetch a small resource
  const testConnection = async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('Connection test failed:', error);
      return false;
    }
  };

  const retryConnection = async (): Promise<boolean> => {
    const connectionOk = await testConnection();

    if (connectionOk && !isOnline) {
      setIsOnline(true);
      setLastOnlineTime(new Date());
    } else if (!connectionOk && isOnline) {
      setIsOnline(false);
      setHasBeenOffline(true);
    }

    return connectionOk;
  };

  useEffect(() => {
    const handleOnline = async () => {
      // Double-check with actual network request
      const connectionOk = await testConnection();

      if (connectionOk) {
        setIsOnline(true);
        setLastOnlineTime(new Date());
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setHasBeenOffline(true);
    };

    // Listen to browser events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also listen to visibility change to re-check connection
    const handleVisibilityChange = () => {
      if (!document.hidden && !isOnline) {
        // When tab becomes visible and we think we're offline, test connection
        retryConnection();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial connection test if browser says we're online
    if (navigator.onLine && !isOnline) {
      testConnection().then((connectionOk) => {
        if (connectionOk) {
          setIsOnline(true);
          setLastOnlineTime(new Date());
        }
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOnline]);

  return {
    isOnline,
    isOffline: !isOnline,
    hasBeenOffline,
    lastOnlineTime,
    retryConnection,
  };
};

export default useOnlineStatus;
